import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { joinByToken, validateInviteToken } from '../api/api';
import { useAuth } from '../context/AuthContext';

const JoinOrganizationPage = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const token = new URLSearchParams(location.search).get('token') || '';

  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setError('Invitation token is missing.');
        setLoading(false);
        return;
      }
      try {
        const res = await validateInviteToken(token);
        setInvite(res?.data?.data || null);
      } catch (e) {
        setError(e?.response?.data?.message || 'This invitation is invalid or expired.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token]);

  const handleJoin = async () => {
    setJoining(true);
    setError('');
    try {
      await joinByToken({ token });
      setSuccess('Organization joined successfully. Redirecting to dashboard...');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to join organization.');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg,#fef9ec 0%,#fff 50%,#fdf0f4 100%)' }}>
      <div className="w-full max-w-md bg-white rounded-3xl border shadow-xl p-6" style={{ borderColor: '#f9b8ca' }}>
        <h1 className="text-2xl font-extrabold text-slate-900">Join Organization</h1>
        <p className="text-sm text-slate-500 mt-1">Accept your invitation to join your team workspace.</p>

        {loading && <p className="mt-5 text-sm text-slate-500">Validating invitation...</p>}

        {!loading && error && (
          <div className="mt-5 px-4 py-3 rounded-xl text-sm" style={{ background: '#fce0e8', border: '1px solid #f9b8ca', color: '#c44569' }}>
            {error}
          </div>
        )}

        {!loading && !error && invite && (
          <div className="mt-5 space-y-3">
            <div className="rounded-xl border p-4" style={{ borderColor: '#f9b8ca', background: '#fdf0f4' }}>
              <p className="text-xs text-slate-500">Organization</p>
              <p className="text-sm font-bold text-slate-900">{invite.organization_name || invite.organization?.name || 'Organization'}</p>
              <p className="text-xs text-slate-500 mt-2">Role</p>
              <p className="text-sm font-semibold text-slate-800">{(invite.org_role || invite.role || 'employee').replace(/_/g, ' ')}</p>
            </div>

            {success && (
              <div className="px-4 py-3 rounded-xl text-sm" style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#166534' }}>
                {success}
              </div>
            )}

            {isAuthenticated ? (
              <button
                onClick={handleJoin}
                disabled={joining}
                className="w-full py-2.5 rounded-xl text-white font-semibold text-sm transition disabled:opacity-60"
                style={{ background: '#c44569' }}
              >
                {joining ? 'Joining...' : 'Join Organization'}
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">Please sign in first, then open this invitation link again.</p>
                <Link to="/login" className="block w-full text-center py-2.5 rounded-xl text-white font-semibold text-sm" style={{ background: '#c44569' }}>
                  Go to Login
                </Link>
                <Link to="/register" className="block w-full text-center py-2.5 rounded-xl border text-sm font-semibold" style={{ borderColor: '#f9b8ca', color: '#7a1f3d' }}>
                  Create New Account
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinOrganizationPage;
